import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Flower, Eye, EyeSlash } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    api
      .get("/auth/verify-token", { params: { token } })
      .then((r) => {
        if (r.data.valid) {
          setEmail(r.data.email || "");
          setState("valid");
        } else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password minimal 6 karakter");
    if (password !== confirm) return setError("Konfirmasi password tidak cocok");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password berhasil dibuat. Silakan login.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF7FF] px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-[#7C4DE0] text-white shadow-md">
            <Flower size={34} weight="duotone" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-800">Buat Password Baru</h1>
          {email && <p className="mt-1 text-sm text-slate-500">{email}</p>}
        </div>

        {state === "checking" && (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" />
          </div>
        )}

        {state === "invalid" && (
          <div data-testid="reset-invalid" className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-center">
            <p className="text-sm text-rose-700">
              Tautan tidak valid atau sudah kadaluarsa. Silakan minta tautan baru.
            </p>
            <Link to="/forgot-password" className="mt-3 inline-block rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white">
              Minta Tautan Baru
            </Link>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">Password Baru</label>
              <div className="relative">
                <input
                  data-testid="reset-password-input"
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  className={inputCls + " pr-11"}
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">Konfirmasi Password</label>
              <input
                data-testid="reset-confirm-input"
                type={show ? "text" : "password"}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Ulangi password"
                className={inputCls}
              />
            </div>
            {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p>}
            <button
              data-testid="reset-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-[#7C4DE0] py-3 text-sm font-bold text-white transition-all hover:bg-[#6A38C7] active:scale-95 disabled:opacity-60"
            >
              {loading ? "Menyimpan..." : "Simpan Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
